import { useState } from 'react';
import { ChartBar, Clock } from '@/core/ui/icons';
import { BarList, Card, ErrorState, FilterMenu, Skeleton, type FilterOption } from '@/core/ui';
import { useActionCatalog, useActivitySummary } from '../application/queries';
import { categoryShares, deviceLabel, exactTime, lastSeenText, type Category } from '../domain/activity';
import { ActivityTimeline } from './ActivityTimeline';

/** A atividade de uma pessoa: resumo dos últimos 30 dias e a linha do tempo dela. */
export function PersonActivity({ userId }: { userId: string }) {
  const summary = useActivitySummary(userId);
  const catalog = useActionCatalog();
  const [category, setCategory] = useState<Category | 'todas'>('todas');

  const categories: FilterOption<Category | 'todas'>[] = [
    { value: 'todas', label: 'Todas' },
    ...(summary.data?.by_category ?? []).map((c) => ({
      value: c.category as Category,
      label: c.label,
      count: c.count,
    })),
  ];
  const labels = new Map((catalog.data?.categories ?? []).map((c) => [c.key, c.label]));

  return (
    <div className="space-y-6">
      {summary.isPending ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : summary.isError ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : (
        <Card className="grid gap-6 p-5 md:grid-cols-[16rem_1fr]">
          <div>
            <h2 className="truncate text-lg font-semibold">{summary.data.user_name ?? 'Pessoa'}</h2>
            <dl className="mt-3 space-y-3 text-sm">
              <div>
                <dt className="flex items-center gap-1 text-xs text-fg-subtle">
                  <ChartBar aria-hidden className="size-3.5" />
                  Ações nos últimos 30 dias
                </dt>
                <dd className="mt-0.5 text-2xl font-semibold tabular-nums">
                  {summary.data.total.toLocaleString('pt-BR')}
                </dd>
              </div>
              <div>
                <dt className="flex items-center gap-1 text-xs text-fg-subtle">
                  <Clock aria-hidden className="size-3.5" />
                  Visto por último
                </dt>
                <dd
                  className="mt-0.5"
                  title={summary.data.last_seen_at ? exactTime(summary.data.last_seen_at) : undefined}
                >
                  {lastSeenText(summary.data.last_seen_at)}
                  {summary.data.last_device && (
                    <span className="block text-xs text-fg-subtle">
                      {deviceLabel(summary.data.last_device)}
                    </span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
          <div>
            <h3 className="mb-3 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
              Por categoria
            </h3>
            {summary.data.total === 0 ? (
              <p className="py-6 text-sm text-fg-subtle">Nenhuma ação nos últimos 30 dias.</p>
            ) : (
              <BarList
                items={categoryShares(summary.data).map((c) => ({
                  key: c.category,
                  label: labels.get(c.category) ?? c.label,
                  value: c.count,
                  percent: c.percent,
                }))}
              />
            )}
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold">Linha do tempo</h2>
        <FilterMenu label="Categoria" value={category} options={categories} onChange={setCategory} />
      </div>
      <ActivityTimeline
        filters={{ userId, category: category === 'todas' ? undefined : category }}
        emptyDescription="Esta pessoa ainda não tem atividade registrada no portal."
      />
    </div>
  );
}
